import React, { useMemo } from 'react';
import { ButtonIcon } from './ButtonIcon';
import { ButtonText } from './ButtonText';
import { ButtonImage } from './ButtonImage';
import { useStateBasedProps } from './useStateBasedProps';
import { IconProps, TextProps, ImageProps, StateBasedProps } from '../Button/ButtonContent';

interface ButtonRenderProps {
  contentOrder?: string[] | StateBasedProps<string[]>;
  icon?: string | StateBasedProps<string>;
  iconStyle?: 'filled' | 'outlined' | StateBasedProps<'filled' | 'outlined'>;
  iconScale?: number | StateBasedProps<number>;
  iconColor?: string | StateBasedProps<string>;
  iconProps?: IconProps | StateBasedProps<IconProps>;
  text?: string | StateBasedProps<string>;
  textProps?: TextProps | StateBasedProps<TextProps>;
  imageUrl?: string;
  imageProps?: ImageProps | StateBasedProps<ImageProps>;
  isHovered: boolean;
  hoverEnabled: boolean;
  colorScheme?: string;
  height: number;
}

export const ButtonRender: React.FC<ButtonRenderProps> = ({
  contentOrder,
  icon,
  iconStyle,
  iconScale,
  iconColor,
  iconProps,
  text,
  textProps,
  imageUrl,
  imageProps,
  isHovered,
  hoverEnabled,
  colorScheme,
  height,
}) => {
  const currentContentOrder = useStateBasedProps(
    Array.isArray(contentOrder) ? { default: contentOrder } : contentOrder,
    isHovered,
    hoverEnabled,
  ) || ['icon'];

  const contentItems = useMemo(() => {
    return currentContentOrder.filter((item) => {
      switch (item) {
        case 'icon':
          return !!icon;
        case 'text':
          return !!text;
        case 'image':
          return !!imageUrl;
        default:
          return false;
      }
    });
  }, [currentContentOrder, icon, text, imageUrl]);

  const hasMultipleItems = contentItems.length > 1;

  const renderContent = (item: string, index: number) => {
    switch (item) {
      case 'icon':
        return (
          <ButtonIcon
            key={`icon-${index}`}
            icon={icon}
            iconStyle={iconStyle}
            iconScale={iconScale}
            iconColor={iconColor}
            iconProps={iconProps}
            isHovered={isHovered}
            hoverEnabled={hoverEnabled}
            hasMultipleItems={hasMultipleItems}
          />
        );
      case 'text':
        return (
          <ButtonText
            key={`text-${index}`}
            text={text}
            textProps={textProps}
            isHovered={isHovered}
            hoverEnabled={hoverEnabled}
            colorScheme={colorScheme}
            hasMultipleItems={hasMultipleItems}
            height={height}
          />
        );
      case 'image':
        return (
          <ButtonImage
            key={`image-${index}`}
            imageUrl={imageUrl}
            imageProps={imageProps}
            isHovered={isHovered}
            hoverEnabled={hoverEnabled}
            hasMultipleItems={hasMultipleItems}
          />
        );
      default:
        return null;
    }
  };

  return <>{currentContentOrder.map((item, index) => renderContent(item, index))}</>;
};
